import type { WebLocale } from '@/enums/web-locale';
import type { RouteRecordRaw } from 'vue-router';

import { CollectionType } from '@/enums/collection-type';
import PressPage from '@/pages/PressPage.vue';
import TvtimeImporterPage from '@/pages/TvtimeImporterPage.vue';
import { RefreshToken, StaticPage } from '@/lazy-load-components';

export function makeStaticRoutes(currentLocale: WebLocale): RouteRecordRaw[] {
	return [
		makePseudonymIdRoute(currentLocale),
		makeTvRoute(currentLocale),
		makeTvRegistrationRoute(currentLocale),
		makeGlobalPrivacyPolicyRoute(currentLocale),
		makePrivacyPolicyRoute(currentLocale),
		makeTermsZendeskRoute(currentLocale),
		makeFaqRoute(currentLocale),
		...makeApplicationsRoutes(currentLocale),
		...makeAboutRoutes(currentLocale),
		...makeTalentRoutes(currentLocale),
		...makeImprintRoutes(currentLocale),
		...makePressRoutes(currentLocale),
		makeRefreshTokenRoute(),
		makeStreamingAPIRoute(currentLocale),
		makeServiceIntegrationRoute(),
		makeTvTimeImporterRoute(currentLocale),
	];
}

// pseudonym-ID redirect - global to locale
function makePseudonymIdRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.pseudonymId',
		path: `/${currentLocale}/pseudonym-ID`,
		component: StaticPage,
		meta: {
			hasFilterBar: false,
		},
	};
}

// tv registration redirect to local
function makeTvRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.tv',
		path: '/tv',
		meta: {
			hasFilterBar: false,
		},
		redirect: to => {
			return `/${currentLocale}${to.path}`;
		},
	};
}

// tv registration redirect to local
function makeTvRegistrationRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.tv.code',
		path: '/tv/:code',
		children: [],
		props: true,
		meta: {
			hasFilterBar: false,
		},
		redirect: to => {
			return `/${currentLocale}${to.path}`;
		},
	};
}

function makeGlobalPrivacyPolicyRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.privacy.global',
		path: '/privacy-policy',
		meta: {
			hasFilterBar: false,
		},
		redirect: to => {
			// country specific slugs
			const countryMap: any = {
				hk: '私隱政策',
				tw: '私隱政策',
			};

			if (countryMap[currentLocale]) {
				return `/${currentLocale}/${countryMap[currentLocale]}`;
			}

			return `/${currentLocale}${to.path}`;
		},
	};
}

// Privacy Policy Zendesk redirect
function makePrivacyPolicyRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.privacy.zendesk',
		path: `/${currentLocale}/privacy-policy`,
		children: [],
		beforeEnter(to) {
			if (import.meta.client) {
				location.href = `https://www.${JW_CONFIG.DOMAIN}${to.fullPath}`;
				return;
			}
		},
		meta: {
			reload: true,
		},
	};
}

// Terms Zendesk redirect
function makeTermsZendeskRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.terms.zendesk',
		path: `/${currentLocale}/terms`,
		children: [],
		beforeEnter(to) {
			if (import.meta.client) {
				location.href = `https://www.${JW_CONFIG.DOMAIN}${to.fullPath}`;
				return;
			}
		},
		meta: {
			reload: true,
		},
	};
}

// FAQ
function makeFaqRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.faq',
		path: `/${currentLocale}/faq`,
		children: [],
		beforeEnter(to) {
			if (import.meta.client) {
				location.href = `https://www.${JW_CONFIG.DOMAIN}${to.fullPath}`;
				return;
			}
		},
		meta: {
			reload: true,
		},
	};
}

// Apps
function makeApplicationsRoutes(currentLocale: WebLocale): RouteRecordRaw[] {
	const slugs = ['apps', 'aplicaciones', 'Apps', 'applications', 'のアプリ'];

	return slugs.map((slug, i) => ({
		name: i === 0 ? 'app.apps' : `app.apps.${slug}`,
		path: `/${currentLocale}/${slug}`,
		component: StaticPage,
		meta: { canonicalName: 'app.apps' },
	}));
}

// About
function makeAboutRoutes(currentLocale: WebLocale): RouteRecordRaw[] {
	const slugs = ['about', 'about-us', 'ueber-uns', 'quienes-somos', 'a-propos', 'sobre-nos'];

	return slugs.map((slug, i) => ({
		name: i === 0 ? 'app.about' : `app.about.${slug}`,
		path: `/${currentLocale}/${slug}`,
		component: StaticPage,
		meta: { canonicalName: 'app.about' },
	}));
}

// Talent
function makeTalentRoutes(currentLocale: WebLocale): RouteRecordRaw[] {
	const slugs = ['talent', 'Talent'];

	return slugs.map((slug, i) => ({
		name: i === 0 ? 'app.talent' : `app.talent.${slug}`,
		path: `/${currentLocale}/${slug}`,
		component: StaticPage,
	}));
}

// Imprint
function makeImprintRoutes(currentLocale: WebLocale): RouteRecordRaw[] {
	const slugs = ['imprint', 'Impressum', 'aviso-legal', 'infos-legales', '出版說明'];

	return slugs.map((slug, i) => ({
		name: i === 0 ? 'app.imprint' : `app.imprint.${slug}`,
		path: `/${currentLocale}/${slug}`,
		component: StaticPage,
		meta: { canonicalName: 'app.imprint' },
	}));
}

function makePressRoutes(currentLocale: WebLocale): RouteRecordRaw[] {
	const slugs = ['press', 'presse', 'prensa', 'imprensa'];

	return slugs.map((slug, i) => ({
		name: i === 0 ? 'app.press' : `app.press.${slug}`,
		path: `/${currentLocale}/${slug}`,
		component: PressPage,
		meta: {
			tab: 'static',
			collectionType: CollectionType.TITLELIST,
		},
	}));
}

// Refresh Token
function makeRefreshTokenRoute(): RouteRecordRaw {
	return {
		name: 'app.refreshtoken',
		path: '/refresh',
		component: RefreshToken,
	};
}

function makeStreamingAPIRoute(currentLocale: WebLocale): RouteRecordRaw {
	return {
		name: 'app.streamingapi',
		path: `/${currentLocale}/JustWatch-Streaming-API`,
		component: StaticPage,
	};
}

function makeServiceIntegrationRoute(): RouteRecordRaw {
	return {
		name: 'app.serviceintegration',
		path: '/us/service-integration',
		component: StaticPage,
	};
}

// TVTime Importer
function makeTvTimeImporterRoute(currentLocale: WebLocale): RouteConfig {
	return {
		name: 'app.tvtimeImporter',
		path: `/${currentLocale}/tvtime-importer`,
		component: TvtimeImporterPage,
		meta: {
			hasFilterBar: false,
		},
	};
}
